import { gql, useMutation, useQuery } from '@apollo/client';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  HStack,
  Link,
  Spacer,
  Tag,
  VStack,
} from '@chakra-ui/react';
import { useToast } from '@chakra-ui/react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';

import {
  AdminPage,
  AdminPageContent,
  AdminPageHeader,
  AdminPageHeading,
} from '@/components/AdminPage';
import { TabLink, TabLinkList } from '@/components/TabLink';
import { HideGroupButton } from '@/components/group-form/HideGroupButton';
import { ECommunityVisibility } from '@/graphql-types/globalTypes';
import { AdminTopBarNavMenuFrame } from '@/imports/ui/admin/layouts/AdminTopBarNavMenuFrame';
import { Card } from '@/imports/ui/chakra/components/Card';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { UnexpectedError } from '@/imports/ui/chakra/components/UnexpectedError';
import { ChevronLeftIcon, EyeIcon } from '@/imports/ui/chakra/feather';

import {
  GroupDetailPageLayout_GroupQuery,
  GroupDetailPageLayout_GroupQueryVariables,
} from './graphql-types/GroupDetailPageLayout_GroupQuery';

export const getAdminGroupIdFromRouter = (router: {
  query: { [key: string]: string | string[] | undefined };
  asPath: string;
}): string | undefined => {
  const fromQuery = router.query.groupId;
  if (typeof fromQuery === 'string' && fromQuery && fromQuery !== 'new') {
    return fromQuery;
  }

  const fromPath = router.asPath
    .split('?')[0]
    .match(/^\/admin\/community\/groups\/([^/]+)/)?.[1];

  if (fromPath && fromPath !== 'new') {
    return fromPath;
  }

  return undefined;
};

const SYNC_GROUP_MEMBERS = gql`
  mutation GroupDetailPageLayout_SyncGroupMembers(
    $input: EmailMarketingIntegrationConstantContactSyncGroupMembersInput!
  ) {
    emailMarketingIntegrationConstantContactSyncGroupMembers(input: $input) {
      success
    }
  }
`;

const GROUP_QUERY = gql`
  query GroupDetailPageLayout_GroupQuery(
    $input: GroupInput!
    $subgroupsInput: SubgroupsInput
  ) {
    group(input: $input) {
      result {
        _id
        parentGroup {
          _id
          name
        }
        name
        settings {
          communityVisibility
          constantContactListId
        }
        ...HideGroupButton_GroupFragment
      }
    }
  }
  ${HideGroupButton.groupFragment}
`;

export const GroupDetailPageLayout: React.FC<
  React.PropsWithChildren<{ groupId?: string }>
> = ({ children, groupId: groupIdProp }) => {
  const router = useRouter();
  const groupId = groupIdProp ?? getAdminGroupIdFromRouter(router);
  const toast = useToast();

  const [syncGroupMembers] = useMutation(SYNC_GROUP_MEMBERS);

  const { error, data, loading } = useQuery<
    GroupDetailPageLayout_GroupQuery,
    GroupDetailPageLayout_GroupQueryVariables
  >(GROUP_QUERY, {
    skip: !groupId,
    variables: {
      input: {
        _id: groupId ?? '',
      },
      subgroupsInput: { filter: { includeAll: true } },
    },
  });

  const group = data?.group.result;

  const handleSyncClick = async () => {
    try {
      const result = await syncGroupMembers({
        variables: {
          input: {
            groupId: groupId ?? '',
          },
        },
      });

      if (
        result.data?.emailMarketingIntegrationConstantContactSyncGroupMembers
          .success
      ) {
        toast({
          title: 'Sync successful',
          status: 'success',
          duration: 3000,
        });
      } else {
        toast({
          title: 'Sync failed',
          status: 'error',
          duration: 3000,
        });
      }
    } catch (error) {
      toast({
        title: 'Sync failed',
        status: 'error',
        duration: 3000,
      });
    }
  };

  return (
    <AdminTopBarNavMenuFrame>
      <AdminPage maxWidth="container.md" mx="auto">
        {!groupId || loading ? (
          <Loading />
        ) : error || !group ? (
          <UnexpectedError />
        ) : (
          <>
            <AdminPageHeader>
              <VStack w="full" alignItems="start" spacing={4}>
                <Breadcrumb separator={<ChevronLeftIcon />} color="blue.600">
                  <BreadcrumbItem>
                    <BreadcrumbLink
                      as={NextLink}
                      href="/admin/community/groups"
                    >
                      All Groups
                    </BreadcrumbLink>
                  </BreadcrumbItem>

                  {group.parentGroup && (
                    <BreadcrumbItem>
                      <BreadcrumbLink
                        as={NextLink}
                        href={`/admin/community/groups/${group.parentGroup._id}/settings`}
                      >
                        {group.parentGroup.name}
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                  )}

                  <BreadcrumbItem>
                    <BreadcrumbLink
                      as={NextLink}
                      href={`/admin/community/groups/${group._id}/settings`}
                    >
                      {group.name}
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                </Breadcrumb>
                <HStack w="full">
                  <AdminPageHeading noOfLines={1} wordBreak="break-all">
                    {group.name}
                  </AdminPageHeading>
                  {group.settings.communityVisibility ===
                    ECommunityVisibility.HIDDEN && (
                    <Tag variant="solid" colorScheme="gray">
                      Hidden
                    </Tag>
                  )}
                  <Spacer />
                  <HideGroupButton group={group} />
                </HStack>
                <HStack w="full" justify="space-between">
                  <Link
                    as={NextLink}
                    href={`/community/groups/${group._id}/activity`}
                    display="flex"
                    alignItems="center"
                    gap="2"
                  >
                    <EyeIcon />
                    <span>See Page</span>
                  </Link>
                  {group.settings?.constantContactListId && (
                    <Button
                      onClick={handleSyncClick}
                      size="sm"
                      colorScheme="purple"
                    >
                      Trigger Full Sync with Constant Contact
                    </Button>
                  )}
                </HStack>
              </VStack>
              <Spacer />
            </AdminPageHeader>
            <AdminPageContent>
              <Card px="6" w="full" mb="6">
                <TabLinkList>
                  <TabLink href={`/admin/community/groups/${groupId}/settings`}>
                    Settings
                  </TabLink>
                  {!group.parentGroup?._id && (
                    <TabLink
                      href={`/admin/community/groups/${groupId}/subgroups`}
                    >
                      Subgroups
                    </TabLink>
                  )}
                  <TabLink href={`/admin/community/groups/${groupId}/members`}>
                    Members
                  </TabLink>
                  <TabLink
                    href={`/admin/community/groups/${groupId}/opted-out-members`}
                  >
                    Opted Out Members
                  </TabLink>
                  <TabLink
                    href={`/admin/community/groups/${groupId}/pending-invites`}
                  >
                    Rejoin Invites
                  </TabLink>
                </TabLinkList>
              </Card>

              {children}
            </AdminPageContent>
          </>
        )}
      </AdminPage>
    </AdminTopBarNavMenuFrame>
  );
};
